import type {
  IIndexedSelectionItemModel,
  IReasoningModeOption,
} from "../types/chat";
import { EMicState, EReasoningMode } from "../types/chat";

export const FOLDER_NAME_MAX_LENGTH = 100 as const;

// NOTE: NSAI-5681 — socket event carrying one "connect this app" prompt for an
// assistant message. Mirrors INTEGRATION_CONNECT_SOCKET_EVENT on the server
// (chat/tools/integration-connect/integration-connect.constants.ts).
export const INTEGRATION_CONNECT_PROMPT_SOCKET_EVENT =
  "integrationConnectPrompt" as const;

// NOTE: NSAI-5473 — socket event marking one tool step finished. Mirrors
// TOOL_CALL_END_SOCKET_EVENT on the server (chat/constant/chat-gateway.constants.ts).
export const TOOL_CALL_END_SOCKET_EVENT = "toolCallEnd" as const;

// NOTE: Max length of a folder's custom instructions. Mirrors the server-side
// MAX_CHAT_FOLDER_CUSTOM_INSTRUCTIONS_LENGTH DTO cap.
export const FOLDER_CUSTOM_INSTRUCTIONS_MAX_LENGTH = 10000 as const;

export const DEFAULT_CHAT_LIST_LIMIT = 20;

export const DEFAULT_CHAT_HISTORY_LIMIT = 20;
export const DEFAULT_CHAT_HISTORY_OFFSET = 0;

export const DEFAULT_CHAT_LIST_OFFSET = 0;

export const DEFAULT_CHAT_FOLDER_LIST_LIMIT = 20;
export const DEFAULT_CHAT_FOLDER_LIST_OFFSET = 0;

// NOTE: Max length for displayed file/folder names; beyond this show truncation or info icon.
export const MAX_DISPLAY_FILE_NAME_LENGTH = 70;

// NOTE: Same as above but for mobile (narrower screens); show info icon for shorter names.
export const MAX_DISPLAY_FILE_NAME_LENGTH_MOBILE = 50;

export const MAX_DISPLAY_ASSISTANT_FILE_NAME_LENGTH = 50;

export const CHAT_QUERY_KEY = "chat";
export const CHAT_LIST_QUERY_KEY = "chat-list";
// NOTE: NSAI-5678 — query-key segment that scopes a chat-list cache to one
// EChatListKind, so the sidebar's regular and assistant lists are cached and
// paginated apart while a `[CHAT_LIST_QUERY_KEY]` prefix still reaches both.
export const CHAT_LIST_KIND_QUERY_KEY_SEGMENT = "kind";
export const CHAT_HISTORY_QUERY_KEY = "chat-history";
// NOTE: NSAI-4336 — shared so web and mobile name the cross-chat semantic
// search cache identically. An array rather than a string because both clients
// append the needle as a further segment.
export const SEMANTIC_CHAT_SEARCH_QUERY_KEY = [
  "chats",
  "search",
  "semantic",
] as const;
export const ARCHIVED_CHAT_LIST_QUERY_KEY = "archived-chat-list";

// NOTE: NSAI-5908 / NSAI-5909 — the per-token socket events. The first three
// are parked in the stream buffer; `promptImproveDelta` never touches the chat
// history but is just as frequent, so it must not drain the buffer either.
// Every other socket event drains the buffer before it is handled, which is
// the ordering rule both clients rest on — hence one shared list rather than a
// copy per platform.
export const CHAT_STREAM_DELTA_EVENT = "messageDelta";
export const CHAT_STREAM_REASONING_EVENT = "reasoningStep";
export const CHAT_STREAM_CONTENT_UPDATE_EVENT = "messageContentUpdate";
export const PROMPT_IMPROVE_DELTA_EVENT = "promptImproveDelta";
export const STREAM_BUFFER_PASSTHROUGH_EVENTS: ReadonlySet<string> = new Set([
  CHAT_STREAM_DELTA_EVENT,
  CHAT_STREAM_REASONING_EVENT,
  CHAT_STREAM_CONTENT_UPDATE_EVENT,
  PROMPT_IMPROVE_DELTA_EVENT,
]);
export const CHAT_FOLDER_LIST_QUERY_KEY = "chat-folder-list";

// NOTE: Prefix for temporary/optimistic messages before server confirms
export const TEMP_MESSAGE_PREFIX = "temp-";

// NOTE: NSAI-6790 — prompt-history menu rows. Long enough that a one-line row
// always fills, short enough that a 50 KB pasted log does not travel through
// every menu render; the full text stays on the entry for labels and tooltips.
export const PROMPT_HISTORY_PREVIEW_MAX_LENGTH = 160;

// NOTE: NSAI-5116 — internal bucket key under which the websocket services and
// the send-stall watchdogs track a NEW chat's first send (no chatId exists
// until the server emits chatCreated). Client-local only: it is a Map/timer
// key inside each tab's service singleton and never travels on the wire, so
// two tabs (or two users) each track their own pending first send without any
// shared state. Only one new-chat send exists per tab at a time — the
// composer is locked by the pending-first-send state.
export const NEW_CHAT_SEND_BUCKET_KEY = "__new-chat-send__";

export const CHAT_SETTINGS_QUERY_KEY = "organization-chat-settings";

export const SEARCH_CHATS_DEBOUNCE_DELAY_MS = 500;

// NOTE: NSAI-4334 — minimum needle length before the keyword chat search is
// allowed to run. `chat.title` and `message.content` are encrypted at rest, so
// the server cannot match them with a SQL LIKE and instead runs a bounded
// decrypt-and-scan. That scan costs the same for a short needle as a long one
// (it always walks to its hard cap; finding matches does not end it early),
// while a single character matches nearly every row — the largest possible
// result set for the least useful query. An empty query stays valid: it means
// "browse the chat list", not "search".
export const CHAT_SEARCH_MIN_QUERY_LENGTH = 2;

// NOTE: MIME types the platform treats as ZIP archives. Mirrors the server-side
// ZIP_MIME_TYPES; some Windows browsers report `application/x-zip-compressed`.
// Single source of truth for the ZIP entries in DOC_FILE_ACCEPT so UI gating
// (see ChatInput) stays in sync when the set changes.
export const ZIP_MIME_TYPES: readonly string[] = [
  "application/zip",
  "application/x-zip-compressed",
];

export const DOC_FILE_ACCEPT = {
  // NOTE: Text documents. NSAI-2796 — browsers report legacy .doc as
  // "application/msword"; "application/vnd.ms-word" is non-standard, kept for
  // upstream integrations that emit it.
  "application/msword": [".doc"],
  "application/vnd.ms-word": [".doc"],
  "application/vnd.google-apps.document": [".gdoc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],

  // NOTE: PDF
  "application/pdf": [".pdf"],

  // NOTE: Text
  "text/plain": [".txt"],

  // NOTE: Rich text
  "application/rtf": [".rtf"],

  // NOTE: Spreadsheets
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.google-apps.spreadsheet": [".gsheet"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],

  // NOTE: CSV / TSV
  "text/csv": [".csv"],
  "text/tab-separated-values": [".tsv"],

  // NOTE: JSON / JSONL
  "application/json": [".json"],
  "application/jsonlines": [".jsonl"],

  // NOTE: Presentations
  "application/vnd.google-apps.presentation": [".gslides"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    ".pptx",
  ],

  // NOTE: Markdown
  "text/markdown": [".md", ".mdx"],

  // NOTE: HTML (NSAI-6765) — attached as a document the model reads as source;
  // only generated pages open in the live preview.
  "text/html": [".html", ".htm"],

  // NOTE: Archives — unpacked server-side by the hardened zip extractor.
  // `application/x-zip-compressed` is what some Windows browsers report for .zip.
  "application/zip": [".zip"],
  "application/x-zip-compressed": [".zip"],
};

export const IMAGE_FILE_ACCEPT = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/svg+xml": [".svg"],
  "image/webp": [".webp"],
  "image/heic": [".heic"],
  "image/heif": [".heif"],
};

export type TImageFileAccept =
  (typeof IMAGE_FILE_ACCEPT)[keyof typeof IMAGE_FILE_ACCEPT][number];

export type TDocFileAccept =
  (typeof DOC_FILE_ACCEPT)[keyof typeof DOC_FILE_ACCEPT][number];

export const CLIPBOARD_FILE_ACCEPT: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/heic": "heic",
  "image/heif": "heif",
  "application/pdf": "pdf",
} as const;

// NOTE: Fallback value for MAX_FILE_UPLOAD_COUNT. The actual limit is configured via the
// MAX_FILE_UPLOAD_COUNT env var and served by the /app-config endpoint at runtime.
// This constant is only used as a TypeScript safety net — the server validates the env var
// at startup via zod, so the runtime value is always available.
export const DEFAULT_MAX_FILE_UPLOAD_COUNT = 10;

// NOTE: Client-side guard for chat attachments (NSAI-4154) — mirrors the maxSize
// pattern already used by logo/avatar/probe/video uploads elsewhere in the app.
export const MAX_CHAT_ATTACHMENT_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50MB

// NOTE: NSAI-5691 — the longest chat message a send may carry, counted as
// `text.length` of the body that goes out: the typed instruction plus every
// pasted-text card with its markers. Nothing bounded it before, and every step
// between the send and the model — PII scanning, moderation, retrieval — grows
// with the text, so a 100k–500k-character paste sat on the typing dots for
// minutes with no answer and no error. 100 000 characters is ~25k tokens, about
// 20 PII detector chunks. Keep in sync with MAX_CHAT_MESSAGE_LENGTH in
// server/src/chat/constant/chat.constant.ts — the server does not import
// frontend-shared, and it refuses a longer send on its own.
export const MAX_CHAT_MESSAGE_LENGTH = 100_000;

// NOTE: NSAI-5691 — the composer shows the running count from this share of
// MAX_CHAT_MESSAGE_LENGTH, the same 80% the field counters use (NSAI-6001), so
// an ordinary message carries no extra line.
export const CHAT_MESSAGE_LENGTH_NOTICE_RATIO = 0.8;

// NOTE: NSAI-7647 — PowerPoint decks carry embedded media, so a .pptx gets its
// own 500 MB cap; every other type keeps the 50 MB one above. Pick the cap for a
// file through `resolveChatAttachmentMaxBytes`, never by reading either constant
// directly. Mirrors the server's CHAT_PRESENTATION_ATTACHMENT_MAX_FILE_SIZE_BYTES.
export const MAX_CHAT_PRESENTATION_ATTACHMENT_FILE_SIZE_BYTES =
  500 * 1024 * 1024; // 500MB
export const CHAT_PRESENTATION_ATTACHMENT_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";

// NOTE: Maximum number of models allowed in a multi-model chat
export const MAX_MULTI_MODEL_COUNT = 5;

// NOTE: NSAI-2721 (follow-up to PR review) — cap successful improvements
// per draft. The real reason for this isn't API abuse (the backend already
// has a per-user 10-per-60s window for that —
// IMPROVE_PROMPT_RATE_LIMIT_MAX / _WINDOW_SECONDS). It's a product-quality
// guardrail:
//
//   Each `improvePrompt` call rewrites whatever the draft says NOW. Since
//   NSAI-6242 the server also reads the chat the draft belongs to, so a
//   follow-up is no longer improved as though it were a first message — but
//   the user's ORIGINAL phrasing and the prior improvements are still not
//   part of the request. So every click is the model rewriting whatever it
//   last produced, without memory of what the user actually wanted, and the
//   drift the cap guards against is unchanged. After 3–4 rounds the prompt
//   drifts noticeably from the user's initial idea ("write a poem about
//   cats" → "compose a lyrical sonnet about felidae" → …). Capping at 5
//   forces the user to either commit (Send), redirect (edit manually),
//   or accept that further auto-edits won't get closer to their intent.
//
// "Per draft" is naturally a frontend concept (the backend doesn't see
// the composer state). The counter lives in each client's improve-prompt
// hook and resets when `value === ""` — i.e. when the user has either sent
// the message (Send clears the input) or manually cleared it.
//
// NOTE: NSAI-5157 — lifted here from `web/src/pages/chat/constants` so the
// two clients cannot drift to different caps. The number is product policy,
// not a web layout detail. The sentence above also used to claim a "5s
// cooldown" that has never existed in `improve-prompt-rate-limiter.service.ts`
// — corrected here rather than carried across, because it is what sends people
// looking for a limiter they can trigger by tapping twice.
export const IMPROVE_PROMPT_MAX_PER_DRAFT = 5;

// NOTE: NSAI-4665 — a `?? []` fallback on `chatData.originalModels` mints a
// fresh array on every render for as long as the chat query has no data, and
// permanently for an assistant chat. The chat history memoises its ordered uuid
// list on that reference, so a new identity rebuilds every block and hands each
// row new props. Share one module-level empty list instead. Not frozen: it is
// passed into props typed as a mutable array, where a runtime freeze would turn
// an in-place use into a throw rather than the no-op it is today.
export const EMPTY_ORIGINAL_MODELS: IIndexedSelectionItemModel[] = [];

export const APPROXIMATE_WORDS_PER_TOKEN = 0.75;

// NOTE: File size thresholds for formatting (in bytes). NSAI-4566/NSAI-4403 —
// decimal (SI) steps of 1000, NOT binary 1024, because the labels we print are
// "KB"/"MB". Dividing by 1024 under a "KB" label produced KiB values wearing
// decimal units: a 39,724-byte file read "38.8 KB" in our chip while the iOS
// share sheet and macOS Finder — both decimal — read "40 KB" for the same file.
// Display-only; these are not upload caps. Size limits live in their own
// constants (e.g. CHAT_ATTACHMENT_MAX_FILE_SIZE_BYTES) and are unaffected.
export const KB_THRESHOLD = 1000;
export const MB_THRESHOLD = 1000 * 1000;

// NOTE: NSAI-5042 — maximum time to buffer pending messages before expiration.
// Must exceed the worst-case zombie-socket detection window (server
// pingInterval 25s + pingTimeout 120s ≈ 145s), otherwise a message buffered on
// a half-open socket is guaranteed to expire before the reconnect that would
// retry it.
export const PENDING_MESSAGE_TTL_MS = 180000;

// NOTE: NSAI-5042 — ack timeout for the connectionHealthCheck liveness probe
// sent after a sendMessage ack timeout, to distinguish a zombie socket from a
// server-side failure.
export const HEALTH_CHECK_ACK_TIMEOUT_MS = 5000;

// NOTE: NSAI-5042 — a buffered message is retried at most this many times
// before the failure is surfaced to the user instead.
export const PENDING_MESSAGE_MAX_ATTEMPTS = 3;

// NOTE: NSAI-5042 — how long the optimistic typing dots may exist with no
// server event before the UI surfaces a send failure. Must exceed the ack
// timeout plus a reconnect+flush cycle.
export const SEND_STALL_TIMEOUT_MS = 45000;
export const SEND_STALL_TIMEOUT_MS_WITH_FILES = 90000;

// NOTE: NSAI-5042 — minimum interval between auth-exception-driven token
// refresh + reconnect cycles. Guards against a refresh/reconnect loop if a
// server keeps rejecting guarded events for a reason a fresh token cannot fix.
export const AUTH_RECONNECT_THROTTLE_MS = 10000;

// NOTE: NSAI-5042 — mirrors WS_AUTH_UNAUTHORIZED_ERROR_CODE in
// apps/nsai/server/src/constants/ws-auth.constants.ts (same pattern as
// LICENSE_EXPIRED_ERROR_CODE). Set on WsException payloads thrown by
// WsJwtAuthGuard and the handshake middleware so clients can react with a
// token refresh + reconnect instead of ignoring the "exception" event.
export const WS_AUTH_UNAUTHORIZED_ERROR_CODE = "WS_AUTH_UNAUTHORIZED" as const;

export const AUTH_ERROR_PATTERNS = [
  "unauthorized access",
  "invalid token",
  "expired token",
  "expired jwt",
  "authentication failed",
  "jwt expired",
  "invalid jwt",
  "not authenticated",
  "missing authentication",
  "session expired",
] as const;

// NOTE: Large/multi-file payloads can take longer to be fully received/parsed on the server
// (especially behind staging proxies). If we time out too aggressively we trigger an
// unnecessary reconnect, which can cause the client to miss chatCreated/stream events.
export const MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITH_FILES = 60000;
// NOTE: Default message acknowledgement timeout without files
export const MESSAGE_ACKNOWLEDGEMENT_TIMEOUT_MS_WITHOUT_FILES = 10000;

// NOTE: Must match Socket.IO server `maxHttpBufferSize` (see `server/src/chat/constant/chat.constant.ts`).
// This limit applies to a single Engine.IO message payload.
// Base64-encoded data URLs expand binary size by ~33%, so the effective raw file size limit is lower.
export const DEFAULT_MAX_HTTP_BUFFER_SIZE_BYTES = 2 * 100 * 1024 ** 2; // 200MB

export const MIC_STATUS_I18N_KEY_BY_STATE = {
  [EMicState.Recording]: "input.mic.status.recording",
  [EMicState.Transcribing]: "input.mic.status.transcribing",
} as const;

export const MIC_ACTION_LABEL_I18N_KEY_BY_STATE = {
  [EMicState.Idle]: "input.mic.idle",
  [EMicState.Recording]: "input.mic.recording",
  [EMicState.Transcribing]: "input.mic.transcribing",
} as const;

export const MIC_ERROR_I18N_KEY_BY_TYPE = {
  unsupported: "input.mic.errors.unsupported",
  permissionDenied: "input.mic.errors.permission_denied",
  recordingFailed: "input.mic.errors.recording_failed",
  transcriptionFailed: "input.mic.errors.transcription_failed",
  recordingLimitExceeded: "input.mic.errors.recording_limit_exceeded",
  noSpeechDetected: "input.mic.errors.no_speech_detected",
} as const;

export const DEFAULT_AUDIO_MIME_TYPE = "audio/webm";
export const DEFAULT_AUDIO_FILE_EXTENSION = "webm";
export const DEFAULT_AUDIO_FILE_PREFIX = "audio-recording";

export const DEFAULT_RECORDING_LIMIT_IN_MS = 5 * 60 * 1000; // 5 minutes
export const DEFAULT_RECORDING_LIMIT_IN_MS_ON_PREMISE = 60 * 60 * 1000; // 1 hour

// NOTE: RMS threshold for centered time-domain samples from the web recorder.
// Values below this are treated as silence/background noise.
//
// NSAI-6259 — this was 8, which silently discarded most recordings. The samples
// are 0..255 centred on 128, so full scale is 127 and an RMS of 8 works out at
// 20*log10(8*sqrt(2)/127) = -21 dBFS. Ordinary speech peaks around -20 dBFS, so
// the gate sat AT the level it was supposed to be well below: anything but a
// loud voice close to the mic was thrown away with "no speech detected", which
// is the "it cannot hear me" report.
//
// 2 is -33 dBFS, which lines up with the mobile recorder's own -35 dBFS gate
// (SILENCE_DETECTION_DB_THRESHOLD below, equivalent to RMS 1.6) — the two
// platforms had no business disagreeing by 5x on what counts as speech. Room
// tone sits near -50 dBFS (RMS 0.3), so this still clears background noise by
// more than 20 dB, and three consecutive checks are still required.
export const SILENCE_DETECTION_WEB_RMS_THRESHOLD = 2;

// NOTE: Decibel threshold for mobile silence detection (expo-av metering, range -160 to 0).
// Values below this are considered silence/background noise.
export const SILENCE_DETECTION_DB_THRESHOLD = -35;

// NOTE: How often (ms) the web recorder checks audio levels for silence detection.
export const SILENCE_DETECTION_CHECK_INTERVAL_MS = 100;

// NOTE: Require speech to stay above the threshold across multiple checks to
// avoid fan noise or brief spikes being treated as real speech.
export const SILENCE_DETECTION_MIN_CONSECUTIVE_SPEECH_CHECKS = 3;

// NOTE: FFT window size for the web AnalyserNode. Larger values give better
// frequency resolution but slower response. 2048 is the Web Audio API default.
export const SILENCE_DETECTION_FFT_SIZE = 2048;

// NOTE: Smoothing factor for the web AnalyserNode (0 = no smoothing, 1 = max).
// A low value makes the analyser react quickly to transient speech.
export const SILENCE_DETECTION_SMOOTHING_TIME_CONSTANT = 0.2;

// NOTE: Marker stored in the error column when an empty assistant message is
// converted on server restart (connection lost mid-stream).
export const CONNECTION_INTERRUPTED_ERROR = "CONNECTION_INTERRUPTED";

// NOTE: Localized texts the server persists (as message and/or error content)
// when the user stops a generation. There is no marker constant for this case,
// so the client detects a cancellation by matching these strings. Keep in sync
// with server/src/i18n/*/chat.json → "message_generation_cancelled".
export const MESSAGE_GENERATION_CANCELLED_CONTENTS: readonly string[] = [
  "Message generation was cancelled",
  "Генерация сообщения была отменена",
  "تم إلغاء توليد الرسالة",
];

// NOTE: Keep this literal in sync with server/src/token-budget/token-budget.constants.ts.
export const TOKEN_BUDGET_EXCEEDED_ERROR_CODE =
  "TOKEN_BUDGET_EXCEEDED" as const;

// NOTE: NSAI-5037 — keep in sync with
// server/src/model-token-limits/model-token-limits.constants.ts. Marks a request
// rejected for exceeding the per-model INPUT cap configured for its
// project/organization — a permanent condition for that payload, unlike
// TOKEN_BUDGET_EXCEEDED which clears when the period resets.
export const MODEL_TOKEN_LIMIT_EXCEEDED_ERROR_CODE =
  "MODEL_TOKEN_LIMIT_EXCEEDED" as const;

// NOTE: NSAI-4636 — tag on the socket "error" payload when a send failed because
// the selected model is unavailable. Keep this literal in sync with
// server/src/chat/services/message-workflow/chat-model-unavailable.error.ts.
export const MODEL_UNAVAILABLE_ERROR_CODE = "MODEL_UNAVAILABLE" as const;

// NOTE: NSAI-7404 — tag on the socket "error" payload when a send was refused
// because the chat is locked to a PII trusted model that can no longer answer.
// Keep this literal in sync with
// apps/nsai/server/src/chat/services/pii/pii-trusted-model-unavailable.error.ts.
export const PII_TRUSTED_MODEL_UNAVAILABLE_ERROR_CODE =
  "PII_TRUSTED_MODEL_UNAVAILABLE" as const;

// NOTE: NSAI-5810 — keep this literal in sync with
// server/src/chat/constant/generation-failure.constants.ts. Marks a turn that
// failed because OUR code faulted (a stack overflow, a null dereference), as
// opposed to the provider failing or a cap being hit. The engine's own wording
// for such a crash means nothing to a reader — a failed deck build used to
// answer the user with the bare string "Maximum call stack size exceeded" — so
// this code is what tells a client to render the failure card instead of
// printing the content it was handed.
export const GENERATION_FAILURE_ERROR_CODE = "GENERATION_FAILURE" as const;

// NOTE: NSAI-5820 — where a chat starts talking about its own context. Shared so
// web and mobile warn at the same point.
//
// 0.75 is chosen to leave the user room to ACT rather than merely to know: at
// three-quarters there are still several turns before anything is dropped, which
// is what makes condensing a choice instead of damage control. Warning earlier
// would make the banner near-permanent on any working session and train people
// to ignore it — the failure mode that makes a warning worse than none.
export const CONTEXT_PRESSURE_APPROACHING_RATIO = 0.75;

// NOTE: 0.9 — close enough that the next long turn may not fit. The copy stops
// suggesting and starts recommending; still not an error, because the turn does
// still work.
export const CONTEXT_PRESSURE_CRITICAL_RATIO = 0.9;

// NOTE: NSAI-5820 — socket event carrying one turn's context pressure. Mirrors
// CONTEXT_PRESSURE_SOCKET_EVENT on the server
// (chat/constant/context-pressure.constants.ts).
export const CONTEXT_PRESSURE_SOCKET_EVENT = "contextPressure" as const;

// NOTE: NSAI-6542 — the Auto confirmation card's socket contract; must match
// AUTO_CONFIRMATION_*_SOCKET_EVENT in the server's auto-capability.constants.
export const AUTO_CONFIRMATION_REQUEST_SOCKET_EVENT =
  "autoConfirmationRequest" as const;
export const AUTO_CONFIRMATION_RESOLVED_SOCKET_EVENT =
  "autoConfirmationResolved" as const;
export const AUTO_CONFIRMATION_RESPONSE_SOCKET_EVENT =
  "autoConfirmationResponse" as const;

// NOTE: NSAI-6974 — socket event naming the images of a user message whose
// description had personal data masked. Mirrors PII_IMAGE_MASKED_SOCKET_EVENT
// on the server (chat/constant/pii-intent-routing.constants.ts).
export const PII_IMAGE_MASKED_SOCKET_EVENT = "piiImageMasked" as const;

// NOTE: NSAI-6883 — socket event listing the attachments a turn answered
// without, or with only the beginning of. Mirrors
// ATTACHMENTS_OMITTED_SOCKET_EVENT on the server
// (chat/constant/file-context-shedding.constants.ts).
export const ATTACHMENTS_OMITTED_SOCKET_EVENT = "attachmentsOmitted" as const;

// NOTE: NSAI-7345 — socket event naming the attachments of a user message whose
// PDF yielded too little text for its page count. Mirrors
// ATTACHMENT_TEXT_EXTRACTION_WARNING_SOCKET_EVENT on the server
// (chat/constant/text-extraction-warning.constants.ts).
export const ATTACHMENT_TEXT_EXTRACTION_WARNING_SOCKET_EVENT =
  "attachmentTextExtractionWarning" as const;

export const HEIC_FILE_EXTENSION_REGEX = /\.hei[cf]$/i;
export const HEIC_MIME_TYPES = new Set(["image/heic", "image/heif"]);

// NOTE: Extension a HEIC gets after conversion. Shared because attachment
// duplicate detection normalizes names through it on every platform, so a
// re-picked .heic still matches the .jpeg twin it converted into.
export const CONVERTED_HEIC_FILE_EXTENSION = ".jpeg";

export const DEFAULT_FILE_NAME = "file";

// NOTE: What a browser reports when it cannot resolve a MIME type for the file
// it just handed us. Treated as "no information" rather than a real type — see
// `resolveFileMimeType`.
export const GENERIC_BINARY_MIME_TYPE = "application/octet-stream" as const;

// NOTE: Maps common file extensions to MIME types for icon resolution.
// Knowledge sources only have fileName (no MIME type), so we derive it.
export const EXTENSION_MIME_MAP: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  csv: "text/csv",
  txt: "text/plain",
  md: "text/markdown",
  mdx: "text/markdown",
  html: "text/html",
  htm: "text/html",
  rtf: "application/rtf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  svg: "image/svg+xml",
  zip: "application/zip",
};

export const REASONING_MODE_OPTIONS: readonly IReasoningModeOption[] = [
  { mode: EReasoningMode.THINKING, labelKey: "input.reasoning_thinking" },
  { mode: EReasoningMode.FAST, labelKey: "input.reasoning_fast" },
] as const;

// NOTE: Preparation-progress status rotation, shared by the web and mobile
// MessagePreparationProgress components. The backend wording for
// "preparing_context"/"generating_response" reads too technical, so the UI never
// surfaces it — it opens on a "preparation_rotating_statuses" pool snippet
// immediately and keeps rotating. "processing_files" instead shows the backend
// message first, then rotates after a short delay so long uploads don't feel frozen.
export const PREPARATION_ROTATING_STATUS_INITIAL_DELAY_MS = 3000;
export const PREPARATION_ROTATING_STATUS_INTERVAL_MS = 2500;
export const PREPARATION_IMMEDIATE_ROTATION_STATUSES = [
  "preparing_context",
  "generating_response",
] as const;
export const PREPARATION_DELAYED_ROTATION_STATUS = "processing_files";

// NOTE: NSAI-4262 — name of the server-side in-chat image generation tool. Must
// stay in sync with CHAT_IMAGE_GENERATION_TOOL_NAME in the backend
// (apps/nsai/server/src/chat/constant/image-generation.constants.ts); there is
// no shared module between the server and the clients, so the literal is
// mirrored here. Shared by web and mobile: both have to know that a message
// carrying this tool call is still waiting for a picture, not finished.
export const IMAGE_GENERATION_TOOL_NAME = "generateImage" as const;
